import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { motion } from "framer-motion";
import { 
  Shield, 
  ShieldCheck, 
  ShieldOff,
  Smartphone,
  Key,
  Copy,
  CheckCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export const TwoFactorSetup = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: QR code, 2: Verify, 3: Backup codes
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    initiateSetup();
  }, []);

  const initiateSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to initiate 2FA setup");
      toast.error("Failed to setup 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: verifyCode });
      setStep(3);
      toast.success("2FA enabled successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleComplete = () => {
    if (onSuccess) onSuccess();
    if (onClose) onClose();
  };

  if (loading && !setupData) {
    return (
      <div className="p-8 flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400 mb-4" />
        <p className="text-slate-500">Setting up two-factor authentication...</p>
      </div>
    );
  }

  if (error && !setupData) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={initiateSetup}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step 1: QR Code */}
      {step === 1 && setupData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Step 1: Scan QR Code
            </h3>
            <p className="text-sm text-slate-500">
              Use your authenticator app (Google Authenticator, Authy, etc.) to scan this QR code
            </p>
          </div>

          <div className="flex justify-center">
            <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
              <img 
                src={setupData.qr_code} 
                alt="2FA QR Code" 
                className="w-48 h-48"
                data-testid="2fa-qr-code"
              />
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="text-xs text-slate-500 mb-2">Can't scan? Enter this code manually:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono break-all">
                {setupData.secret}
              </code>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => copyToClipboard(setupData.secret)}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <Button 
            onClick={() => setStep(2)} 
            className="w-full"
            data-testid="2fa-next-step-btn"
          >
            Next: Verify Code
          </Button>
        </motion.div>
      )}

      {/* Step 2: Verify */}
      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Step 2: Verify Setup
            </h3>
            <p className="text-sm text-slate-500">
              Enter the 6-digit code from your authenticator app to verify setup
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <Label htmlFor="verifyCode">Verification Code</Label>
              <Input
                id="verifyCode"
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                autoFocus
                data-testid="2fa-verify-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 text-center">{error}</p>
            )}

            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setStep(1)}
                className="flex-1"
              >
                Back
              </Button>
              <Button 
                type="submit" 
                disabled={verifyCode.length !== 6 || loading}
                className="flex-1"
                data-testid="2fa-verify-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Enable"}
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Step 3: Backup Codes */}
      {step === 3 && setupData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              2FA Enabled Successfully!
            </h3>
            <p className="text-sm text-slate-500">
              Save these backup codes in a secure place. You can use them to access your account if you lose your authenticator.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> Each backup code can only be used once. Store them securely - they won't be shown again.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {setupData.backup_codes.map((code, index) => (
                <code 
                  key={index}
                  className="bg-white px-3 py-2 rounded border text-center font-mono text-sm"
                >
                  {code}
                </code>
              ))}
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-3"
              onClick={() => copyToClipboard(setupData.backup_codes.join("\n"))}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy All Codes
            </Button>
          </div>

          <Button 
            onClick={handleComplete} 
            className="w-full"
            data-testid="2fa-complete-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Done
          </Button>
        </motion.div>
      )}
    </div>
  );
};

export const TwoFactorDisable = ({ onClose, onSuccess }) => {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDisable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/auth/2fa/disable`, { password, code });
      toast.success("2FA disabled successfully");
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to disable 2FA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleDisable} className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldOff className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Disable Two-Factor Authentication
        </h3>
        <p className="text-sm text-slate-500">
          This will remove the extra security layer from your account. Enter your password and current 2FA code to confirm.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              data-testid="2fa-disable-password"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="code">Authenticator Code</Label>
          <Input
            id="code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            className="text-center tracking-widest font-mono"
            maxLength={6}
            required
            data-testid="2fa-disable-code"
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 text-center">{error}</p>
      )}

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button 
          type="submit" 
          variant="destructive"
          disabled={!password || code.length !== 6 || loading}
          className="flex-1"
          data-testid="2fa-disable-confirm-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disable 2FA"}
        </Button>
      </div>
    </form>
  );
};

export const TwoFactorStatus = ({ refreshTrigger }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [regeneratingCodes, setRegeneratingCodes] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState("");
  const [newBackupCodes, setNewBackupCodes] = useState(null);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`);
      setStatus(response.data);
    } catch (err) {
      console.error("Failed to fetch 2FA status:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [refreshTrigger]);

  const handleRegenerateBackupCodes = async (e) => {
    e.preventDefault();
    setRegeneratingCodes(true);

    try {
      const response = await axios.post(`${API}/auth/2fa/regenerate-backup-codes`, { 
        code: regenerateCode 
      });
      setNewBackupCodes(response.data.backup_codes);
      toast.success("New backup codes generated");
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to regenerate codes");
    } finally {
      setRegeneratingCodes(false);
      setRegenerateCode("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <>
      <div className="swiss-card p-6" data-testid="2fa-status-card">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              status?.enabled ? 'bg-green-100' : 'bg-slate-100'
            }`}>
              {status?.enabled ? (
                <ShieldCheck className="w-6 h-6 text-green-600" />
              ) : (
                <Shield className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Two-Factor Authentication</h3>
              <p className="text-sm text-slate-500 mt-1">
                {status?.enabled 
                  ? "Your account is protected with 2FA (Required)" 
                  : "2FA is required for all accounts"}
              </p>
              {status?.enabled && (
                <p className="text-xs text-slate-400 mt-2">
                  {status.backup_codes_remaining} backup codes remaining
                </p>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            {status?.enabled ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowBackupCodes(true)}
                data-testid="2fa-backup-codes-btn"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Backup Codes
              </Button>
            ) : (
              <Button 
                onClick={() => setShowSetup(true)}
                data-testid="2fa-enable-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Enable 2FA
              </Button>
            )}
          </div>
        </div>
        
        {/* Info message about mandatory 2FA */}
        {status?.enabled && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> Two-Factor Authentication is mandatory for all NBR Trade Mark Monitoring accounts and cannot be disabled.
            </p>
          </div>
        )}
      </div>

      {/* Setup Dialog */}
      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Protect your account with an authenticator app
            </DialogDescription>
          </DialogHeader>
          <TwoFactorSetup 
            onClose={() => setShowSetup(false)} 
            onSuccess={() => {
              setShowSetup(false);
              fetchStatus();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={(open) => {
        setShowBackupCodes(open);
        if (!open) {
          setNewBackupCodes(null);
          setRegenerateCode("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Backup Codes</DialogTitle>
            <DialogDescription>
              {newBackupCodes 
                ? "Save these new backup codes securely" 
                : "Generate new backup codes (current codes will be invalidated)"}
            </DialogDescription>
          </DialogHeader>
          
          {newBackupCodes ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {newBackupCodes.map((code, index) => (
                  <code 
                    key={index}
                    className="bg-slate-50 px-3 py-2 rounded border text-center font-mono text-sm"
                  >
                    {code}
                  </code>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  navigator.clipboard.writeText(newBackupCodes.join("\n"));
                  toast.success("Copied to clipboard");
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All Codes
              </Button>
              <Button 
                className="w-full"
                onClick={() => {
                  setShowBackupCodes(false);
                  setNewBackupCodes(null);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleRegenerateBackupCodes} className="space-y-4">
              <div>
                <Label htmlFor="regenerateCode">Enter current authenticator code</Label>
                <Input
                  id="regenerateCode"
                  type="text"
                  value={regenerateCode}
                  onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="text-center tracking-widest font-mono"
                  maxLength={6}
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowBackupCodes(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={regenerateCode.length !== 6 || regeneratingCodes}
                  className="flex-1"
                >
                  {regeneratingCodes ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Generate New Codes"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
